/**
 * Royal Gold LED overlay — premium navy & gold variant of the main LED output.
 *
 *   - 16:9 canvas, container-query scaling, stadium-glow + corner accents
 *     mirrors the organiser-provided artifact design 1:1.
 *   - Pulls live data from the same `usePublicAuction` + `useOverlayState`
 *     hooks the classic LED uses — current player, bid, bidder team, LED
 *     logo, superadmin-selected extra detail fields.
 *   - Reuses `SaleBanner` so sold / unsold announcements still fire.
 */
import React from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { resolveImageUrl } from "@/components/ImageInput";
import {
  usePublicAuction, useOverlayState, useSaleAnnouncement, SaleBanner,
  FIELD_LABELS, readPlayerField, getLedDisplayFields, brandLogoFor, PHOTO_FALLBACK,
} from "./_shared";

const royalCss = `
.royal-root {
  min-height: 100vh;
  background: #030812;
  display: grid; place-items: center;
  padding: 12px; color: #fff;
  font-family: 'Oswald', sans-serif;
}
.royal-stage {
  width: min(100%, calc((100vh - 24px) * 16 / 9));
  aspect-ratio: 16/9;
  position: relative; overflow: hidden; isolation: isolate;
  background:
    linear-gradient(135deg, rgba(10,27,50,.82) 0%, rgba(4,16,35,.88) 52%, rgba(7,26,48,.82) 100%),
    url('/royal-gold-bg.jpg') center / cover no-repeat;
  border: 1px solid rgba(255,220,135,.55);
  box-shadow: 0 0 34px rgba(0,0,0,.55);
  container-type: inline-size;
}
.royal-glow { position:absolute; inset:0; z-index:-2;
  background:
    radial-gradient(ellipse at 4% 14%, rgba(255,255,255,.75) 0 1px, transparent 2px),
    radial-gradient(ellipse at 96% 14%, rgba(255,255,255,.75) 0 1px, transparent 2px),
    repeating-radial-gradient(ellipse at 50% 120%, transparent 0 2.1%, rgba(255,255,255,.025) 2.2% 2.35%, transparent 2.5% 4.5%);
}
.royal-glow::before, .royal-glow::after {
  content:""; position:absolute; top:5%; width:19%; height:18%;
  background: repeating-linear-gradient(90deg, rgba(255,255,255,.8) 0 1.2%, rgba(255,231,160,.6) 1.5% 2.2%, transparent 2.5% 5%);
  filter: drop-shadow(0 0 12px rgba(255,220,130,.65));
  opacity:.55; clip-path: polygon(0 10%,100% 0,90% 100%,10% 90%);
}
.royal-glow::before { left:-3%; transform: rotate(8deg); }
.royal-glow::after  { right:-3%; transform: rotate(-8deg); }
.royal-edge-top    { position:absolute; top:2.1%; left:17%; right:17%; height:13%; border:.18cqw solid rgba(222,177,68,.88); border-bottom:0; opacity:.8; clip-path: polygon(0 0,100% 0,94% 100%,6% 100%); }
.royal-edge-bottom { position:absolute; bottom:1.5%; left:7%; right:7%; height:8%; border:.18cqw solid rgba(222,177,68,.88); border-top:0; opacity:.8; clip-path: polygon(5% 0,95% 0,100% 100%,0 100%); }
.royal-corner-l { position:absolute; left:-7%; bottom:3%; width:19%; height:24%; border-top:.4cqw solid #d9ad42; border-right:.4cqw solid #d9ad42; opacity:.8; transform: skewX(-32deg); pointer-events:none; }
.royal-corner-r { position:absolute; right:-7%; bottom:3%; width:19%; height:24%; border-top:.4cqw solid #d9ad42; border-left:.4cqw solid #d9ad42;  opacity:.8; transform: skewX(32deg);  pointer-events:none; }
.royal-brand {
  position:absolute; top:2.5%; left:1.2%; width:6.1%; aspect-ratio:1;
  display:grid; place-items:center;
  border-radius:.45cqw; color:#0c2341; font-size:clamp(7px,.85cqw,14px);
  font-weight:900; text-align:center; padding:2%; overflow:hidden;
}
.royal-brand img { max-width:100%; max-height:100%; object-fit:contain; }
.royal-header {
  position:absolute; top:3.1%; left:14%; right:14%;
  text-align:center; padding:.45% 2% 1.2%;
  border-top:.22cqw solid #d9ad42;
  border-bottom:.22cqw solid rgba(217,173,66,.8);
  background: linear-gradient(90deg, transparent, rgba(4,16,35,.86) 12%, rgba(4,16,35,.9) 88%, transparent);
  clip-path: polygon(5% 0,95% 0,100% 50%,95% 100%,5% 100%,0 50%);
}
.royal-tour {
  font-weight:900; text-transform:uppercase; letter-spacing:.025em;
  color:#ffe7a0; font-size:clamp(15px,3.5cqw,56px); line-height:1.08;
  text-shadow: 0 2px 0 #76541a, 0 0 18px rgba(255,205,89,.16);
  white-space:nowrap; overflow:hidden; text-overflow:none;
}
.royal-tag {
  display:inline-block; margin-top:.6%; padding:.3% 3.5%;
  color:#061326; background: linear-gradient(90deg,#b78326,#fff0b6,#c99a39);
  font-size:clamp(10px,1.8cqw,29px); font-weight:900; letter-spacing:.08em;
  clip-path: polygon(4% 0,96% 0,100% 50%,96% 100%,4% 100%,0 50%);
}
.royal-pnum {
  position:absolute; left:2.2%; top:28%; width:5.2%; aspect-ratio:1;
  border-radius:50%; display:grid; place-items:center;
  border:.28cqw solid #d9ad42; background: linear-gradient(145deg,#132c4b,#061326);
  box-shadow: 0 0 18px rgba(255,204,83,.18);
  font-size:clamp(14px,2.1cqw,34px); font-weight:900;
}
.royal-card {
  position:absolute; left:10%; top:24%; width:28%; height:58%;
  border:.38cqw solid #d9ad42; border-radius:1.1cqw; padding:.65%;
  background: linear-gradient(145deg,rgba(12,34,61,.95),rgba(1,9,21,.97));
  box-shadow: 0 0 20px rgba(240,190,68,.15), inset 0 0 0 .14cqw rgba(255,231,160,.65);
}
.royal-portrait {
  position:absolute; inset:2.5% 2.5% 17.5%; overflow:hidden;
  display:grid; place-items:center;
  background:
    linear-gradient(140deg,rgba(20,52,87,.45),transparent 45%),
    repeating-linear-gradient(135deg,transparent 0 12%,rgba(255,255,255,.025) 12.2% 12.6%,transparent 12.8% 25%),
    #07172c;
  border:.12cqw solid rgba(255,225,143,.6);
}
.royal-portrait img { width:100%; height:100%; object-fit:cover; object-position:center top; }
.royal-portrait svg { width:72%; height:85%; opacity:.42; }
.royal-nameplate {
  position:absolute; left:-1%; right:-1%; bottom:8.2%;
  padding:2.6% 2%; text-align:center;
  background: linear-gradient(100deg,#08172b,#193253,#071326);
  border:.22cqw solid #d9ad42; clip-path: polygon(8% 0,92% 0,100% 50%,92% 100%,8% 100%,0 50%);
  font-size:clamp(12px,2.1cqw,34px); font-weight:900; text-transform:uppercase;
}
.royal-role {
  position:absolute; bottom:1.5%; left:18%; right:18%; text-align:center;
  padding:.9% 1%; background: linear-gradient(90deg,#a9771d,#ffe8a0,#b98727);
  color:#071326; font-size:clamp(9px,1.3cqw,21px); font-weight:900;
  text-transform:uppercase; clip-path: polygon(8% 0,92% 0,100% 100%,0 100%);
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.royal-details {
  position:absolute; left:40.2%; right:4.8%; top:22%;
  display:flex; flex-direction:column; gap:1.8%;
  max-height:46%; overflow:hidden;
}
.royal-row {
  display:flex; align-items:center; min-height:4cqw; height:12%;
  border:.16cqw solid #d9ad42; border-radius:2.5cqw;
  background: linear-gradient(90deg,rgba(8,27,49,.97),rgba(3,14,29,.88));
  box-shadow: inset 0 0 12px rgba(0,0,0,.25);
  overflow:hidden; margin:2px;
}
.royal-row-lbl {
  width:30%; align-self:stretch; display:flex; align-items:center;
  padding-left:3.5%; flex-shrink:0;
  background: linear-gradient(100deg,#9e711e,#f8d879 55%,#b8872c);
  color:#10203a; font-weight:900; font-size:clamp(10px,1.45cqw,24px);
  clip-path: polygon(0 0,100% 0,87% 100%,0 100%); text-transform:uppercase;
}
.royal-row-val {
  padding:0 3%; font-size:clamp(11px,1.55cqw,25px); font-weight:700;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.royal-bid {
  position:absolute; left:48%; top:76%; width:30%; height:26%;
  text-align:center; display:flex; flex-direction:column; align-items:center;
}
.royal-bid-lbl {
  width:100%; display:flex; align-items:center; gap:2%; justify-content:center;
  font-size:clamp(11px,1.6cqw,26px); font-weight:900; letter-spacing:.08em;
}
.royal-bid-lbl::before, .royal-bid-lbl::after {
  content:""; height:.16cqw; flex:1; max-width:20%; background:#d9ad42;
}
.royal-bid-val {
  font-size:50px; line-height:.98; font-weight:1000;
  color:#ffe7a0; letter-spacing:-.04em;
  text-shadow: 0 3px 0 #8b621a, 0 0 28px rgba(255,210,90,.18);
}
.royal-bid-units { font-size:clamp(10px,1.5cqw,24px); font-weight:800; letter-spacing:.28em; color:#fff; }
.royal-bid-team {
  margin-top:.4%; font-size:clamp(10px,1.3cqw,22px); font-weight:800;
  color:#ffe7a0; letter-spacing:.1em; text-transform:uppercase;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:100%;
}
.royal-qr {
  position:absolute; left:2.3%; bottom:3.2%; width:7.6%; height:19%;
  display:flex; flex-direction:column; justify-content:center; align-items:center; gap:5%;
  padding:1%; border:.25cqw solid #d9ad42; border-radius:.8cqw;
  background:#fff; color:#071326; text-align:center;
  font-size:clamp(7px,.85cqw,14px); font-weight:900;
}
.royal-qr svg { width:72%; height:72%; }
.royal-ball {
  position:absolute; right:2.4%; bottom:4.5%; width:6.8%; aspect-ratio:1; border-radius:50%;
  background: radial-gradient(circle at 32% 28%,#ffeab0,#b98425 28%,#251a0b 66%,#050a12 78%);
  border:.2cqw solid rgba(255,220,126,.85); box-shadow: 0 0 24px rgba(255,190,55,.25);
}
.royal-ball::before, .royal-ball::after {
  content:""; position:absolute; inset:15% 42%;
  border-left:.22cqw dashed #fff0bd; border-right:.22cqw dashed #fff0bd;
  border-radius:50%; transform: rotate(28deg);
}
.royal-ball::after { transform: rotate(-28deg); }
.royal-team {
  position:absolute; right:2.4%; bottom:4.5%; width:6.8%; aspect-ratio:1; border-radius:50%;
  background:#fff; border:.2cqw solid rgba(255,220,126,.85);
  box-shadow: 0 0 24px rgba(255,190,55,.35);
  display:grid; place-items:center; overflow:hidden;
}
.royal-team img { width:82%; height:82%; object-fit:contain; }
@media (max-aspect-ratio: 16/9) {
  .royal-stage { width: min(100%, calc((100vw - 24px) * 16 / 9)); }
}
`;

const PLACEHOLDER_PORTRAIT = (
  <svg viewBox="0 0 300 400" aria-hidden="true">
    <path fill="#7890b2" d="M150 24c-47 0-77 37-77 88 0 28 9 48 23 63-3 28-12 42-35 53-31 14-48 28-56 64l-5 72h300l-5-72c-8-36-25-50-56-64-23-11-32-25-35-53 14-15 23-35 23-63 0-51-30-88-77-88z" />
  </svg>
);

export function OverlayLEDRoyal() {
  const { auctionId } = useParams();
  const { auction, teams, players, reload } = usePublicAuction(auctionId);
  const state = useOverlayState(auctionId, reload);
  const announce = useSaleAnnouncement(auctionId, players, teams);

  const cur = state?.current_player;
  const bidder = state?.current_bidder_team;
  const bid = state?.current_bid || 0;
  const brandLogo = brandLogoFor(auction);
  const extras = getLedDisplayFields(auction);
  // Hard-cap at 6 rows so the layout never overflows the detail column.
  const detailFields = ["batting_style", "bowling_style", "mobile", ...extras]
    .filter((v, i, a) => a.indexOf(v) === i)
    .slice(0, 6);

  const photo = cur?.photo_url ? resolveImageUrl(cur.photo_url) : null;
  const bannerImg = auction?.banner_url ? resolveImageUrl(auction.banner_url) : null;
  const bidderLogo = bidder?.logo_url ? resolveImageUrl(bidder.logo_url) : null;
  const streamUrl = `${window.location.origin}/overlay/${auctionId}/stream`;
  const roleAndAge = [cur?.role, cur?.age ? `${cur.age} YRS` : null].filter(Boolean).join(" · ");

  return (
    <div className="royal-root" data-testid="overlay-led-royal">
      <style>{royalCss}</style>
      <main className="royal-stage" aria-label="Cricket auction LED overlay">
        <div className="royal-glow" />
        <div className="royal-edge-top" />
        <div className="royal-edge-bottom" />
        <div className="royal-corner-l" />
        <div className="royal-corner-r" />

        <div className="royal-brand" data-testid="royal-brand">
          {brandLogo ? <img src={brandLogo} alt="" onError={(e) => { e.target.style.display = "none"; }} /> : <>YOUR<br />LOGO</>}
        </div>

        <header className="royal-header">
          <div className="royal-tour" data-testid="royal-tournament">{auction?.name || "TOURNAMENT NAME"}</div>
          <div className="royal-tag">PLAYER AUCTION</div>
        </header>

        {cur?.player_number != null && (
          <div className="royal-pnum" data-testid="royal-player-number">#{cur.player_number}</div>
        )}

        {/* Iter40 #3: with no player on the block, show the tournament banner
            full-screen (same idle behaviour as the classic theme) instead of
            the empty player card + details. */}
        {!cur && bannerImg && (
          <img
            src={bannerImg}
            alt=""
            data-testid="royal-idle-banner"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 1 }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        )}

        <section className="royal-card" aria-label="Player profile" style={!cur && bannerImg ? { display: "none" } : undefined}>
          <div className="royal-portrait" data-testid="royal-portrait">
            {photo ? (
              <img src={photo} alt="" onError={(e) => { e.target.replaceWith(Object.assign(document.createElement("img"), { src: PHOTO_FALLBACK, style: "width:100%;height:100%;object-fit:cover" })); }} />
            ) : PLACEHOLDER_PORTRAIT}
          </div>
          <div className="royal-nameplate" data-testid="royal-player-name">{cur?.name || "PLAYER NAME"}</div>
          {roleAndAge && <div className="royal-role">{roleAndAge}</div>}
        </section>

        <section className="royal-details" aria-label="Player details" style={!cur && bannerImg ? { display: "none" } : undefined}>
          <div className="royal-row">
            <div className="royal-row-lbl">NAME</div>
            <div className="royal-row-val">{cur?.name || "—"}</div>
          </div>
          {detailFields.map((k) => {
            const v = readPlayerField(cur, k);
            return (
              <div className="royal-row" key={k} data-testid={`royal-row-${k}`}>
                <div className="royal-row-lbl">{(FIELD_LABELS[k] || k).replace(/_/g, " ")}</div>
                <div className="royal-row-val">{v != null && v !== "" ? String(v) : "—"}</div>
              </div>
            );
          })}
        </section>

        <section className="royal-bid" aria-label="Current bid" style={!cur && bannerImg ? { display: "none" } : undefined}>
          <div className="royal-bid-lbl">CURRENT BID</div>
          <div className="royal-bid-val" data-testid="royal-bid">{Number(bid).toLocaleString()}</div>
          <div className="royal-bid-units">POINTS</div>
          {/* Iter40: bidder identity is the team logo in the corner badge
              only — no "BY · name" text line. */}
        </section>

        <aside className="royal-qr" aria-label="Fan view QR">
          <QRCodeSVG value={streamUrl} fgColor="#071326" bgColor="#ffffff" level="M" />
          <small>SCAN &amp;<br />WATCH LIVE</small>
        </aside>

        {/* #10: bidder team logo replaces the gold ball while a team holds
            the bid — same behaviour as the classic theme's bidder badge. */}
        {bidderLogo ? (
          <div className="royal-team" data-testid="royal-bidder-logo" title={bidder.name}>
            <img src={bidderLogo} alt={bidder.name} onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
          </div>
        ) : (
          <div className="royal-ball" aria-hidden="true" />
        )}

        <SaleBanner announce={announce} auction={auction} position="center" />
      </main>
    </div>
  );
}
